import { QuickLinks } from "@components/QuickLink";
import { useInstitutionContext } from "@hooks/institutionContext";
import { LoadingSkeleton, Row } from "@narmi/design_system";
import { handleErrorResponse } from "@utils/api";
import NotificationContext from "cerulean/src/NotificationContext";
import { useContext, useEffect, useState } from "react";
import { StaffApiSchema } from "../../api_utils";
import { TopBarPageContainer } from "../../components/PageContainer";
import { NavigationRoutes } from "../../components/navigation/constants/navigationRoutes";
import { useRoutesEnabled } from "../../components/navigation/hooks/useRoutesEnabled";
import { useUserConfiguration } from "../../hooks/useUserConfiguration";
import { AccountOpening } from "./AccountOpening";
import { DigitalBanking } from "./DigitalBanking";
import SurveyPopup from "./SurveyPopup";

export type ApplicationSummaryStatistics =
  StaffApiSchema.components["schemas"]["ApplicationSummaryStatistics"];
export type ApplicationStatistics = StaffApiSchema.components["schemas"]["ApplicationStatistics"];
export type UserStatistics = StaffApiSchema.components["schemas"]["UserStatistics"];
export type ThreadStatistics = StaffApiSchema.components["schemas"]["ThreadStatistics"];
export type TransactionStatistics = StaffApiSchema.components["schemas"]["TransactionStatistics"];

export const Dashboard = () => {
  const { sendNotification } = useContext(NotificationContext);
  const { features } = useUserConfiguration();
  const isRoutesEnabled = useRoutesEnabled();
  const { institution } = useInstitutionContext();

  const [summaryData, setSummaryData] = useState<ApplicationSummaryStatistics | null>(null);
  const [pipelineData, setPipelineData] = useState<ApplicationStatistics | null>(null);
  const [userData, setUserData] = useState<UserStatistics | null>(null);
  const [threadData, setThreadData] = useState<ThreadStatistics | null>(null);

  const isAoEnabled =
    features.includes("business_account_opening") || features.includes("new_account_opening");
  const isDigitalBankingEnabled =
    features.includes("online_banking") || features.includes("mobile_banking");

  useEffect(() => {
    if (!isAoEnabled) {
      return;
    }
    const fetchAoData = async () => {
      try {
        const responses = await Promise.all([
          fetch("/api/dashboard/application_summary_statistics/"),
          fetch("/api/dashboard/application_statistics/"),
        ]);
        responses.forEach(async (res) => {
          if (!res.ok) {
            const errNotification = await handleErrorResponse(res);
            sendNotification(errNotification);
          }
        });
        const [summaryData, pipelineData] = await Promise.all(responses.map((res) => res.json()));
        setSummaryData(summaryData);
        setPipelineData(pipelineData);
      } catch (error) {
        sendNotification({
          type: "negative",
          text: "Failed to fetch account opening statistics",
        });
      }
    };
    fetchAoData();
  }, []);

  useEffect(() => {
    const fetchBankingData = async () => {
      if (!isDigitalBankingEnabled) {
        return;
      }
      try {
        const responses = await Promise.all([
          fetch("/api/dashboard/user_statistics/"),
          fetch("/api/dashboard/thread_statistics/"),
        ]);
        responses.forEach(async (res) => {
          if (!res.ok) {
            const errNotification = await handleErrorResponse(res);
            sendNotification(errNotification);
          }
        });
        const [userData, threadData] = await Promise.all(responses.map((res) => res.json()));
        setUserData(userData);
        setThreadData(threadData);
      } catch (error) {
        sendNotification({
          type: "negative",
          text: "Failed to fetch digital banking statistics",
        });
      }
    };
    fetchBankingData();
  }, []);

  // Transaction data has its own loading state handled in the component
  const bankingDataLoaded = userData && threadData;
  const aoDataLoaded = summaryData && pipelineData;
  const dataPopulated =
    (!isDigitalBankingEnabled && aoDataLoaded) ||
    (!isAoEnabled && bankingDataLoaded) ||
    (bankingDataLoaded && aoDataLoaded);

  return (
    <>
      <TopBarPageContainer maxWidth="960px" title="Dashboard">
        <LoadingSkeleton isLoading={!dataPopulated} lines={5}>
          {dataPopulated ? (
            <Row>
              <Row.Item>
                {summaryData && pipelineData ? (
                  <AccountOpening summaryData={summaryData} pipelineData={pipelineData} />
                ) : null}
                {userData && threadData ? (
                  <DigitalBanking userData={userData} threadData={threadData} />
                ) : null}
              </Row.Item>
              <Row.Item shrink>
                <div style={{ width: "224px" }} className="padding--x--m">
                  <QuickLinks>
                    {isRoutesEnabled[NavigationRoutes.ProductListManager] && (
                      <QuickLinks.Link to={NavigationRoutes.ProductListManager}>
                        Product list manager
                      </QuickLinks.Link>
                    )}
                    {isRoutesEnabled[NavigationRoutes.Personas] && (
                      <QuickLinks.Link to={NavigationRoutes.Personas}>Personas</QuickLinks.Link>
                    )}
                    {isRoutesEnabled[NavigationRoutes.ManageUsers] && (
                      <QuickLinks.Link to={NavigationRoutes.ManageUsers}>
                        {institution.institution_type === "credit_union" ? "Members" : "Customers"}
                      </QuickLinks.Link>
                    )}
                    {isRoutesEnabled[NavigationRoutes.ACHManager] && (
                      <QuickLinks.Link to={NavigationRoutes.ACHManager}>
                        ACH Manager
                      </QuickLinks.Link>
                    )}
                    {isRoutesEnabled[NavigationRoutes.WireManager] && (
                      <QuickLinks.Link to={NavigationRoutes.WireManager}>
                        Wire Manager
                      </QuickLinks.Link>
                    )}
                    {isRoutesEnabled[NavigationRoutes.FedNowManager] && (
                      <QuickLinks.Link to={NavigationRoutes.FedNowManager}>
                        FedNow Manager
                      </QuickLinks.Link>
                    )}
                    {isRoutesEnabled[NavigationRoutes.ManageStaff] && (
                      <QuickLinks.Link to={NavigationRoutes.ManageStaff}>Staff</QuickLinks.Link>
                    )}
                  </QuickLinks>
                </div>
              </Row.Item>
            </Row>
          ) : null}
        </LoadingSkeleton>
      </TopBarPageContainer>
      <SurveyPopup />
    </>
  );
};
